import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js'
import { computeGroupVolume } from '@/graphics/polyhedron-manager'
import { createControls, type OrbitControlPreset } from '@/graphics/camera'
import { addLights, applyEnvironment, addStarfield } from '@/graphics/environment'
import { logger } from '@/app/logger'
import { PolyhedronManager } from '@/graphics/polyhedron-manager'
import { CutService } from '@/game/cut-service'
import { IntegerProblem } from '@/core/cuts'
import { PerformanceProbe, RenderScheduler, ResourceCounters } from '@/game/engine/render-runtime-metrics'

export type QualityProfile = 'default' | 'interactive'

export interface RendererEngineContext {
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  renderer: THREE.WebGLRenderer
  labelRenderer: CSS2DRenderer
  controls: OrbitControls
  polyManager: PolyhedronManager
  cutService: CutService
  renderScheduler: RenderScheduler
  performanceProbe: PerformanceProbe
  resourceCounters: ResourceCounters
  polyhedronGroup: THREE.Group | null
  isAnimationPlaying: boolean
  animationFrameId: number | null
  isDisposed: boolean
  appBusUnsubscribers: Array<() => void>
  boundWindowResizeHandler: (() => void) | null
  boundWindowKeydownHandler: ((event: KeyboardEvent) => void) | null
  qualityProfile?: QualityProfile
  frameCount?: number
  defaultPixelRatio?: number
  interactivePixelRatio?: number
  interactionLoopRafId?: number | null
  interactionResumeTimerId?: number | null
  interactionActive?: boolean
  interactionLastEndAt?: number
  pendingInteractionInputStartAt?: number | null
  pendingInteractionResumeAt?: number | null
  cleanupCuttingPlane(): void
  clearBranchOverlays(): void
}

interface DebugWindow extends Window {
  game?: unknown
  THREE?: typeof THREE
  scene?: THREE.Scene
  renderer?: THREE.WebGLRenderer
}

const INTERACTION_RESUME_DELAY_MS = 180
const INTERACTIVE_LABEL_RENDER_STRIDE = 2

function applyRendererQuality(engine: RendererEngineContext, profile: QualityProfile): void {
  const targetPixelRatio =
    profile === 'interactive'
      ? engine.interactivePixelRatio ?? Math.min(window.devicePixelRatio, 1.25)
      : engine.defaultPixelRatio ?? Math.min(window.devicePixelRatio, 2)
  engine.renderer.setPixelRatio(targetPixelRatio)
  engine.renderer.shadowMap.enabled = profile === 'default'
  engine.qualityProfile = profile
}

export function setQualityProfile(engine: RendererEngineContext, profile: QualityProfile): void {
  if (engine.qualityProfile === profile) return
  applyRendererQuality(engine, profile)
  engine.renderScheduler.invalidate(`quality-${profile}`)
}

export function startInteractionLoop(engine: RendererEngineContext): void {
  if (engine.isDisposed) return
  engine.interactionActive = true

  if (engine.interactionResumeTimerId !== null && engine.interactionResumeTimerId !== undefined) {
    clearTimeout(engine.interactionResumeTimerId)
    engine.interactionResumeTimerId = null
  }

  setQualityProfile(engine, 'interactive')

  if (engine.interactionLoopRafId !== null && engine.interactionLoopRafId !== undefined) {
    return
  }

  const tick = () => {
    if (!engine.interactionActive || engine.isDisposed) {
      engine.interactionLoopRafId = null
      return
    }

    engine.performanceProbe.markStart('camera-drag-frame')
    engine.controls.update()
    engine.renderScheduler.invalidate('camera-interaction')
    engine.renderScheduler.flush()
    engine.performanceProbe.markEnd('camera-drag-frame')

    engine.interactionLoopRafId = requestAnimationFrame(tick)
  }

  engine.interactionLoopRafId = requestAnimationFrame(tick)
}

export function stopInteractionLoop(engine: RendererEngineContext, deferMs: number = INTERACTION_RESUME_DELAY_MS): void {
  if (engine.interactionResumeTimerId !== null && engine.interactionResumeTimerId !== undefined) {
    clearTimeout(engine.interactionResumeTimerId)
    engine.interactionResumeTimerId = null
  }

  const stop = () => {
    engine.interactionActive = false
    if (engine.interactionLoopRafId !== null && engine.interactionLoopRafId !== undefined) {
      cancelAnimationFrame(engine.interactionLoopRafId)
      engine.interactionLoopRafId = null
    }
    setQualityProfile(engine, 'default')
  }

  if (deferMs <= 0) {
    stop()
    return
  }

  engine.interactionResumeTimerId = window.setTimeout(stop, deferMs)
}

export function setupRenderer(engine: RendererEngineContext): void {
  engine.renderer.setSize(window.innerWidth, window.innerHeight)
  engine.defaultPixelRatio = Math.min(window.devicePixelRatio, 2)
  engine.interactivePixelRatio = Math.min(window.devicePixelRatio, 1.25)
  engine.frameCount = 0
  engine.interactionLoopRafId = null
  engine.interactionResumeTimerId = null
  engine.interactionActive = false
  engine.interactionLastEndAt = 0
  engine.pendingInteractionInputStartAt = null
  engine.pendingInteractionResumeAt = null
  engine.qualityProfile = 'default'
  engine.renderer.setPixelRatio(engine.defaultPixelRatio)
  engine.renderer.outputColorSpace = THREE.SRGBColorSpace
  engine.renderer.shadowMap.enabled = true
  engine.renderer.shadowMap.type = THREE.PCFSoftShadowMap
  engine.renderer.toneMapping = THREE.ACESFilmicToneMapping
  engine.renderer.toneMappingExposure = 1.0

  engine.renderer.sortObjects = true

  const canvas = engine.renderer.domElement
  canvas.style.position = 'fixed'
  canvas.style.top = '0'
  canvas.style.left = '0'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.zIndex = '0'
  canvas.style.pointerEvents = 'auto'
  canvas.style.outline = 'none'

  document.body.appendChild(canvas)

  engine.labelRenderer = new CSS2DRenderer()
  engine.labelRenderer.setSize(window.innerWidth, window.innerHeight)
  engine.labelRenderer.domElement.style.position = 'fixed'
  engine.labelRenderer.domElement.style.top = '0px'
  engine.labelRenderer.domElement.style.left = '0px'
  engine.labelRenderer.domElement.style.pointerEvents = 'none'
  engine.labelRenderer.domElement.style.zIndex = '5'
  document.body.appendChild(engine.labelRenderer.domElement)

  if (import.meta.env.DEV) {
    const debugWindow = window as DebugWindow
    debugWindow.game = engine
    debugWindow.THREE = THREE
    debugWindow.scene = engine.scene
    debugWindow.renderer = engine.renderer
  }

  engine.polyManager = new PolyhedronManager(engine.scene)
  engine.cutService = new CutService(engine.polyManager)
  engine.performanceProbe = new PerformanceProbe()
  engine.resourceCounters = new ResourceCounters()
  engine.renderScheduler = new RenderScheduler(() => {
    engine.performanceProbe.markStart('frame-render')
    engine.frameCount = (engine.frameCount ?? 0) + 1
    engine.renderer.render(engine.scene, engine.camera)
    if (engine.pendingInteractionInputStartAt !== null && engine.pendingInteractionInputStartAt !== undefined) {
      engine.performanceProbe.record('camera-input-latency', performance.now() - engine.pendingInteractionInputStartAt)
      engine.pendingInteractionInputStartAt = null
    }
    if (engine.pendingInteractionResumeAt) {
      engine.performanceProbe.record('camera-idle-resume', performance.now() - engine.pendingInteractionResumeAt)
      engine.pendingInteractionResumeAt = null
    }
    const shouldRenderLabels =
      engine.qualityProfile !== 'interactive' ||
      ((engine.frameCount ?? 0) % INTERACTIVE_LABEL_RENDER_STRIDE === 0)
    if (shouldRenderLabels) {
      engine.labelRenderer.render(engine.scene, engine.camera)
    }
    engine.performanceProbe.markEnd('frame-render')
  })
  applyRendererQuality(engine, 'default')
  engine.renderScheduler.invalidate('bootstrap')
}

export function setupCamera(engine: RendererEngineContext): void {
  engine.camera.aspect = window.innerWidth / window.innerHeight
  engine.camera.updateProjectionMatrix()
}

export function setupControls(engine: RendererEngineContext): void {
  const preset: OrbitControlPreset = 'responsive'
  engine.controls = createControls(engine.camera, engine.renderer.domElement, preset)
  engine.controls.addEventListener('start', () => {
    const now = performance.now()
    engine.pendingInteractionInputStartAt = now
    if (engine.interactionLastEndAt && engine.interactionLastEndAt > 0) {
      engine.pendingInteractionResumeAt = now
    }
    startInteractionLoop(engine)
    engine.renderScheduler.invalidate('camera-interaction-start')
  })
  engine.controls.addEventListener('change', () => {
    if (engine.interactionActive) {
      engine.pendingInteractionInputStartAt = performance.now()
    }
    engine.renderScheduler.invalidate('camera-change')
    if (engine.interactionActive) {
      engine.renderScheduler.flush()
    }
  })
  engine.controls.addEventListener('end', () => {
    engine.interactionLastEndAt = performance.now()
    engine.pendingInteractionInputStartAt = null
    stopInteractionLoop(engine, INTERACTION_RESUME_DELAY_MS)
    engine.renderScheduler.invalidate('camera-interaction-end')
  })
}

export function setupLights(engine: RendererEngineContext): void {
  addLights(engine.scene)
}

export function setupEnvironment(engine: RendererEngineContext): void {
  applyEnvironment(engine.scene)
}

export function createAtmosphericParticles(engine: RendererEngineContext): void {
  addStarfield(engine.scene)
}

export function handleResize(engine: RendererEngineContext): void {
  engine.camera.aspect = window.innerWidth / window.innerHeight
  engine.camera.updateProjectionMatrix()
  engine.defaultPixelRatio = Math.min(window.devicePixelRatio, 2)
  engine.interactivePixelRatio = Math.min(window.devicePixelRatio, 1.25)
  applyRendererQuality(engine, engine.qualityProfile ?? 'default')
  engine.renderer.setSize(window.innerWidth, window.innerHeight)
  engine.labelRenderer.setSize(window.innerWidth, window.innerHeight)
  engine.renderScheduler.invalidate('resize')
}

export function resetCamera(engine: RendererEngineContext): void {
  engine.camera.position.set(7, 7, 5)
  engine.camera.lookAt(0, 0, 0)
  engine.controls.target.set(0, 0, 0)
  engine.controls.update()
  engine.renderScheduler.invalidate('camera-reset')
}

export function toggleAnimation(engine: RendererEngineContext): void {
  engine.isAnimationPlaying = !engine.isAnimationPlaying
  if (engine.isAnimationPlaying && engine.animationFrameId === null && !engine.isDisposed) {
    animate(engine)
  } else if (!engine.isAnimationPlaying && engine.animationFrameId !== null) {
    cancelAnimationFrame(engine.animationFrameId)
    engine.animationFrameId = null
  }
  engine.renderScheduler.invalidate('animation-toggle')
}

export function animate(engine: RendererEngineContext): void {
  engine.renderScheduler.invalidate('initial-frame')
  if (!engine.isAnimationPlaying || engine.isDisposed || engine.animationFrameId !== null) {
    return
  }

  const tick = () => {
    if (!engine.isAnimationPlaying || engine.isDisposed) {
      engine.animationFrameId = null
      return
    }
    engine.renderScheduler.invalidate('animation-tick')
    engine.animationFrameId = requestAnimationFrame(tick)
  }

  tick()
}

export function calculateCurrentPolyhedronVolume(engine: RendererEngineContext): number {
  const volume = computeGroupVolume(engine.polyhedronGroup)
  logger.log('Calculated volume from current polyhedron:', volume)
  return volume
}

export function clearVisualization(engine: RendererEngineContext): void {
  if (engine.polyhedronGroup) {
    engine.scene.remove(engine.polyhedronGroup)
    engine.polyhedronGroup = null
  }

  engine.scene.children
    .filter((obj: THREE.Object3D) => obj.name === 'axis-group' || obj instanceof CSS2DObject)
    .forEach((obj: THREE.Object3D) => {
      engine.scene.remove(obj)
    })
  engine.renderScheduler.invalidate('visualization-cleared')
}

export function generateCompleteVisualization(engine: RendererEngineContext, problem: IntegerProblem): void {
  logger.log('Generating complete visualization for problem:', problem)

  try {
    createConvexHull(engine, problem)
    addIntegerPointsWithOptimalSolution(engine, problem)
    engine.renderScheduler.invalidate('visualization-generated')
    logger.log('Complete visualization generated successfully')
  } catch (error) {
    logger.error('Error generating complete visualization:', error)
    throw error
  }
}

export function createConvexHull(engine: RendererEngineContext, problem: IntegerProblem): void {
  logger.log('Creating convex hull...')
  engine.polyhedronGroup = engine.polyManager.buildGroupFromInequalities(problem.inequalities)
  engine.renderScheduler.invalidate('convex-hull')
  logger.log('Convex hull created successfully')
}

export function addIntegerPointsWithOptimalSolution(engine: RendererEngineContext, problem: IntegerProblem): void {
  if (!engine.polyhedronGroup) return
  engine.polyManager.addIntegerPointsAndAxes(engine.polyhedronGroup, problem)
  engine.renderScheduler.invalidate('integer-points')
}

export function createIntegerPointsConvexHull(engine: RendererEngineContext, integerPoints: THREE.Vector3[]): void {
  if (!engine.polyhedronGroup) {
    logger.log('No polyhedron group for convex hull')
    return
  }

  logger.log(`Creating convex hull with ${integerPoints.length} integer points:`)
  integerPoints.forEach((point, i) => {
    logger.log(`  Point ${i}: (${point.x}, ${point.y}, ${point.z})`)
  })

  if (integerPoints.length < 4) {
    logger.log(`Not enough integer points for convex hull (need at least 4, have ${integerPoints.length})`)
    return
  }

  try {
    const convexGeometry = new ConvexGeometry(integerPoints)
    const wireframeGeometry = new THREE.EdgesGeometry(convexGeometry)
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: 0xFFD700,
      linewidth: 2,
      transparent: true,
      opacity: 0.75,
      depthTest: false,
    })

    const convexHullWireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial)
    convexHullWireframe.renderOrder = 100
    engine.polyhedronGroup.add(convexHullWireframe)
    engine.renderScheduler.invalidate('integer-hull')

    convexGeometry.computeBoundingBox()
    logger.log(`✅ Created convex hull for ${integerPoints.length} integer points`)
    logger.log('Convex hull vertices count:', convexGeometry.attributes.position.count)
    logger.log('Convex hull triangles count:', convexGeometry.attributes.position.count / 3)
  } catch (error) {
    logger.warn('❌ Failed to create integer points convex hull:', error)
    createSimpleBoundingBox(engine, integerPoints)
  }
}

export function createSimpleBoundingBox(engine: RendererEngineContext, points: THREE.Vector3[]): void {
  if (!engine.polyhedronGroup || points.length === 0) return

  const box = new THREE.Box3()
  points.forEach((point) => box.expandByPoint(point))

  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())

  const boxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z)
  const boxMaterial = new THREE.MeshBasicMaterial({
    color: 0xFFD700,
    wireframe: true,
    transparent: true,
    opacity: 0.6,
  })

  const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial)
  boxMesh.position.copy(center)
  boxMesh.renderOrder = 8

  engine.polyhedronGroup.add(boxMesh)
  engine.renderScheduler.invalidate('bbox-fallback')
  logger.log('📦 Created fallback bounding box for integer points')
}

export function dispose(engine: RendererEngineContext): void {
  if (engine.isDisposed) {
    return
  }
  engine.isDisposed = true
  engine.isAnimationPlaying = false

  if (engine.animationFrameId !== null) {
    cancelAnimationFrame(engine.animationFrameId)
    engine.animationFrameId = null
  }
  stopInteractionLoop(engine, 0)
  if (engine.interactionResumeTimerId !== null && engine.interactionResumeTimerId !== undefined) {
    clearTimeout(engine.interactionResumeTimerId)
    engine.interactionResumeTimerId = null
  }

  if (engine.boundWindowResizeHandler) {
    window.removeEventListener('resize', engine.boundWindowResizeHandler)
    engine.boundWindowResizeHandler = null
  }
  if (engine.boundWindowKeydownHandler) {
    window.removeEventListener('keydown', engine.boundWindowKeydownHandler)
    engine.boundWindowKeydownHandler = null
  }

  for (const unsubscribe of engine.appBusUnsubscribers) {
    try {
      unsubscribe()
    } catch (error) {
      logger.warn('[Dispose] Failed to unsubscribe app bus listener', error)
    }
  }
  engine.appBusUnsubscribers = []

  try {
    engine.cleanupCuttingPlane()
    engine.clearBranchOverlays()
  } catch (error) {
    logger.warn('[Dispose] Failed to cleanup cutting plane/branch overlays', error)
  }

  if (engine.polyhedronGroup) {
    engine.polyManager?.disposeGroup(engine.polyhedronGroup)
    engine.polyhedronGroup = null
  }

  try {
    (engine.controls as OrbitControls | undefined)?.dispose()
  } catch (error) {
    logger.warn('[Dispose] Failed to dispose controls', error)
  }

  try {
    engine.renderer?.dispose()
  } catch (error) {
    logger.warn('[Dispose] Failed to dispose renderer', error)
  }

  try {
    engine.renderScheduler?.dispose()
  } catch (error) {
    logger.warn('[Dispose] Failed to dispose render scheduler', error)
  }

  const canvas = engine.renderer?.domElement
  if (canvas?.parentElement) {
    canvas.parentElement.removeChild(canvas)
  }
  const labelRoot = engine.labelRenderer?.domElement
  if (labelRoot?.parentElement) {
    labelRoot.parentElement.removeChild(labelRoot)
  }

  const debugWindow = window as DebugWindow
  if (debugWindow.game === engine) {
    debugWindow.game = undefined
  }
  if (debugWindow.scene === engine.scene) {
    debugWindow.scene = undefined
  }
  if (debugWindow.renderer === engine.renderer) {
    debugWindow.renderer = undefined
  }
  if (debugWindow.THREE === THREE) {
    debugWindow.THREE = undefined
  }
}
